import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAdminMessages } from '../../contexts/AdminMessageContext';
import api from '../../services/api';

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PopupContainer = styled.div`
  background: ${props => props.theme.colors.card};
  border-radius: ${props => props.theme.radii.md};
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const PopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const PopupTitle = styled.h2`
  color: ${props => props.theme.colors.text};
  font-weight: 600;
  margin: 0;
  font-size: 1.25rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textDim};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  
  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const MessageIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => {
    switch (props.$type) {
      case 'info': return '#3B82F6';
      case 'warning': return '#F59E0B';
      case 'success': return '#10B981';
      case 'error': return '#EF4444';
      default: return '#6B7280';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  margin: 0 auto 1rem;
`;

const MessageContent = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const MessageTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
`;

const MessageText = styled.p`
  color: ${props => props.theme.colors.textDim};
  line-height: 1.6;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: ${props => props.theme.radii.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  background: ${props => {
    switch (props.$variant) {
      case 'primary': return props.theme.colors.primary;
      case 'secondary': return 'transparent';
      default: return '#6B7280';
    }
  }};
  color: ${props => props.$variant === 'secondary' ? props.theme.colors.text : 'white'};
  border: 1px solid ${props => props.$variant === 'secondary' ? props.theme.colors.border : 'transparent'};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
`;

const AdminMessagePopup = ({ message, onClose, onAction }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { dismissMessage } = useAdminMessages();

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleAction = (action) => {
    if (onAction) {
      onAction(action);
    }
    handleClose();
  };

  const handleDismiss = () => {
    if (dismissMessage) {
      dismissMessage(message._id);
    }
    handleClose();
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'success': return '✅';
      case 'error': return '❌';
      default: return '📢';
    }
  };

  if (!message) return null;

  return (
    <PopupOverlay onClick={handleClose}>
      <PopupContainer 
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: isVisible ? 'scale(1)' : 'scale(0.9)',
          opacity: isVisible ? 1 : 0,
          transition: 'all 0.3s ease'
        }}
      >
        <PopupHeader>
          <PopupTitle>Admin Message</PopupTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </PopupHeader>

        <MessageIcon $type={message.type}>
          {getMessageIcon(message.type)}
        </MessageIcon>

        <MessageContent>
          <MessageTitle>{message.title}</MessageTitle>
          <MessageText>{message.content}</MessageText>
        </MessageContent>

        <ActionButtons>
          <Button 
            $variant="primary"
            onClick={() => handleAction('acknowledge')}
          >
            Got it
          </Button>
          <Button 
            $variant="secondary"
            onClick={handleDismiss}
          >
            Don't show again
          </Button>
        </ActionButtons>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default AdminMessagePopup;
